import {
  isFieldScored,
  isQuestionScored,
  isQuestionVisible,
  isSectionVisible,
  type AnswerMap,
  type TemplateDefinition,
  type AnswerValue,
  type Question,
  type SampleEntryValue,
  type SampleField,
} from './template.js';

/**
 * Read-back helpers for the submitted-audit record view: which answers count
 * as failures ("findings"), and how to render stored answer values as text.
 * Pure — unit tested in findings.test.ts.
 */

/** True when a stored sample entry has any content at all. */
export function entryHasContent(entry: SampleEntryValue): boolean {
  return Object.values(entry).some((v) => v !== undefined && v !== '');
}

/** Scored sub-fields answered "no" in one sample entry. */
export function failedFields(
  question: Question,
  entry: SampleEntryValue,
): SampleField[] {
  return (question.fields ?? []).filter(
    (f) => isFieldScored(f) && entry[f.id] === 'no',
  );
}

/**
 * Does this answer count as a failure? Scored yes/no(/na) questions answered
 * "no"; sample groups with any entry failing a scored sub-field. Unscored
 * questions (gates, text, numbers) never fail.
 */
export function isFailedAnswer(
  question: Question,
  value: AnswerValue | undefined,
): boolean {
  if (!isQuestionScored(question)) return false;
  if (question.type === 'sample_group') {
    if (!Array.isArray(value)) return false;
    return (value as SampleEntryValue[]).some(
      (entry) =>
        entry !== null &&
        typeof entry === 'object' &&
        failedFields(question, entry).length > 0,
    );
  }
  return value === 'no';
}

/**
 * A stored value as text. Answers are conductor-written JSON: anything that
 * isn't a string / number / boolean reads as "—" (String() on an object can
 * throw — "{"toString":1}" — security review 2026-10-27).
 */
function plain(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '—';
}

/** Human-readable rendering of a stored answer (sample groups excluded). */
export function formatAnswerValue(
  question: Question,
  value: AnswerValue | undefined,
): string {
  if (value === undefined || value === '') return '—';
  switch (question.type) {
    case 'yes_no':
    case 'yes_no_na':
      return value === 'yes' ? 'Yes' : value === 'no' ? 'No' : value === 'na' ? 'N/A' : '—';
    case 'percent':
      return typeof value === 'number' || typeof value === 'string' ? `${plain(value)}%` : '—';
    case 'multi_select':
      return Array.isArray(value) && value.length > 0
        ? (value as unknown[]).map(plain).join(', ')
        : '—';
    case 'date':
      return typeof value === 'string' ? dayText(value) : plain(value);
    default:
      return plain(value);
  }
}

/**
 * A date answer as people read it: "2026-10-19" → "Oct 19, 2026" (UI review
 * U41 — on screen, in print and in the server's PDF). A calendar day, so no
 * time zone shifts it; anything else is shown as typed.
 */
export function dayText(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return plain(value);
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (d.getUTCMonth() !== Number(m[2]) - 1 || d.getUTCDate() !== Number(m[3])) return plain(value);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** Same formatting for a sample-entry sub-field value. */
export function formatFieldValue(
  field: SampleField,
  value: string | undefined,
): string {
  if (value === undefined || value === '') return '—';
  if (field.type === 'yes_no' || field.type === 'yes_no_na') {
    return value === 'yes' ? 'Yes' : value === 'no' ? 'No' : value === 'na' ? 'N/A' : '—';
  }
  return plain(value);
}

/** The shown questions answered as a fail, in form order — "Ready to submit" lists them (UI review 3, U182). */
export function failedQuestions(def: TemplateDefinition, answers: AnswerMap): Question[] {
  const out: Question[] = [];
  for (const s of def.sections) {
    if (!isSectionVisible(def, s, answers)) continue;
    for (const q of s.questions) {
      if (isQuestionVisible(def, s, q, answers) && isFailedAnswer(q, answers[q.id])) out.push(q);
    }
  }
  return out;
}
